import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import NavbarDumai from "../../Components/Navbar";
import ProductSkeleton from "../../Components/loading/ProductSkeleton";

const API = process.env.REACT_APP_API_BASE_URL;
const STORAGE = process.env.REACT_APP_STORAGE_URL;

interface ProductImage {
  id: number;
  image: string;
}

interface Category {
  id: number;
  name: string;
}

interface Product {
  id: number;
  name: string;
  price: string;
  discount: number | null;
  final_price: string;
  images: ProductImage[];
  category?: Category;
}

export default function ProductGrid() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [discountOnly, setDiscountOnly] = useState(false);

  const [page, setPage] = useState(1);
  const perPage = 50;

  const [lastPage, setLastPage] = useState(1);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [search, categoryId, discountOnly, page]);

  const fetchCategories = async () => {
    const res = await axios.get(`${API}/api/categories`);
    setCategories(res.data);
  };

  const fetchProducts = async () => {
    setLoading(true);

    const res = await axios.get(`${API}/api/produk`, {
      params: {
        page,
        per_page: perPage,
        search,
        category_id: categoryId,
        discount: discountOnly ? 1 : "",
      },
    });

    setProducts(res.data.data);
    setLastPage(res.data.last_page);

    setLoading(false);
  };

  return (
    <>
      <NavbarDumai />

      <section className="max-w-7xl mx-auto px-4 pt-32 max-sm:pt-24 pb-16 font-signika">
        {/* TITLE */}
        <div className="flex items-center justify-center gap-4 mb-10">
          <div className="h-[1px] bg-gray-300 flex-1"></div>
          <h2 className="text-sm md:text-2xl font-bold uppercase tracking-[0.35em] text-gray-700">Products</h2>
          <div className="h-[1px] bg-gray-300 flex-1"></div>
        </div>

        {/* FILTER */}
        <div className="bg-white border rounded-xl p-4 mb-10 grid md:grid-cols-4 gap-4 shadow-sm">
          {/* SEARCH */}
          <input
            type="text"
            placeholder="Cari produk..."
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            className="border rounded-lg px-4 py-2 w-full"
          />

          {/* CATEGORY */}
          <select
            value={categoryId}
            onChange={(e) => {
              setPage(1);
              setCategoryId(e.target.value);
            }}
            className="border rounded-lg px-4 py-2 w-full">
            <option value="">Semua Category</option>

            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>

          {/* DISCOUNT */}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={discountOnly}
              onChange={(e) => {
                setPage(1);
                setDiscountOnly(e.target.checked);
              }}
            />
            Produk Diskon
          </label>
        </div>

        {/* GRID */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
          {loading ? (
            <ProductSkeleton count={8} />
          ) : (
            products.map((product) => {
              const image = product.images?.length > 0 ? `${STORAGE}/products/${product.images[0].image}` : "/no-image.png";

              const hasDiscount = product.discount && product.discount > 0;

              return (
                <Link key={product.id} to={`/produk/${product.id}`} className="group">
                  <div className="bg-white rounded-xl overflow-hidden border hover:shadow-lg transition">
                    {/* IMAGE */}
                    <div className="relative aspect-square overflow-hidden">
                      <img src={image} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" alt="" />

                      {hasDiscount && <span className="absolute top-3 right-3 bg-red-500 text-white text-sm px-4 py-2 rounded-full">-{product.discount}%</span>}
                    </div>

                    {/* CONTENT */}
                    <div className="p-4 space-y-2">
                      {product.category && <p className="text-[11px] text-gray-400 uppercase">{product.category.name}</p>}

                      <h3 className="text-sm font-semibold text-gray-800 line-clamp-2">{product.name}</h3>

                      <div className="flex gap-2 items-center">
                        {hasDiscount ? (
                          <>
                            <span className="text-gray-400 line-through text-xs">Rp {Number(product.price).toLocaleString()}</span>

                            <span className="text-primary font-bold text-sm">Rp {Number(product.final_price).toLocaleString()}</span>
                          </>
                        ) : (
                          <span className="text-gray-900 font-bold text-sm">Rp {Number(product.price).toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })
          )}
        </div>

        {/* PAGINATION */}
        <div className="flex justify-center mt-12 gap-2 flex-wrap">
          {Array.from({ length: lastPage }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`px-4 py-2 rounded-lg border text-sm
            ${p === page ? "bg-primary text-white border-primary" : "bg-white hover:bg-gray-100"}`}>
              {p}
            </button>
          ))}
        </div>
      </section>
    </>
  );
}
